import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { startOfDay } from "date-fns";
import { useProducts } from "@/entities/settings/hooks";
import { useContract } from "@/pages/contracts/hooks";
import { ContractData, ContractRevenueItem } from "@/pages/contracts/types";
import { useDeals } from "@/pages/deals/hooks";
import { DealStatus } from "@/pages/deals/types";
import { toLocalDate } from "@/utils/datetime";
import {
  BillingFrequency,
  ChargeType,
  PricingModel,
  ProductV2,
  ServiceItem
} from "@/pages/settings/pages/products-v2/types";

export enum TermsBlocker {
  NO_SERVICES = "noServices",
  INCOMPLETE_SERVICES = "incompleteServices"
}

// Adding a service records no dates for a one-time charge unless it's priced
// flexibly. A service missing from the catalog falls back to the line's own
// bills-once signal, as the Terms & Services table does.
const needsDates = (item: ContractRevenueItem, serviceItem?: ServiceItem) => {
  const isOneTime =
    serviceItem?.chargeType === ChargeType.ONE_TIME ||
    item.customBillingFrequency === BillingFrequency.ONCE;
  return !isOneTime || serviceItem?.pricingModel === PricingModel.FLEXIBLE;
};

// A flat-rate service keeps its price in its total, with no unit price or
// quantity. A price of 0 is set; only an empty value blocks.
const hasPrice = (item: ContractRevenueItem) =>
  item.pricePerUnit != null || item.total != null || item.totalLocal != null;

const isServiceComplete = (
  item: ContractRevenueItem,
  serviceItem?: ServiceItem
) =>
  (!needsDates(item, serviceItem) ||
    (!!item.serviceStartDate && !!item.serviceEndDate)) &&
  hasPrice(item);

// A service that has ended and won't renew isn't printed on the proposal, so
// it doesn't hold Generate either.
const hasEnded = (item: ContractRevenueItem, today: Date) =>
  !item.autoRenewal &&
  !!item.serviceEndDate &&
  toLocalDate(item.serviceEndDate) < startOfDay(today);

// Currency and renewal aren't checked: the tab shows an empty currency as USD,
// and the renewal type is derived from the services, empty only without any.
export const termsBlocker = (
  contract: Pick<ContractData, "revenueItems">,
  serviceItems: ReadonlyMap<string, ServiceItem> = new Map(),
  today: Date = new Date()
): TermsBlocker | undefined => {
  const services = (contract.revenueItems ?? []).filter(
    (item) => !item.isDeleted && !hasEnded(item, today)
  );
  if (services.length === 0) return TermsBlocker.NO_SERVICES;
  const complete = services.every((item) =>
    isServiceComplete(
      item,
      item.serviceItemId ? serviceItems.get(item.serviceItemId) : undefined
    )
  );
  return complete ? undefined : TermsBlocker.INCOMPLETE_SERVICES;
};

// Why a proposal can't be generated from the contract yet, if it can't. Reads
// the contract and catalog the Terms & Services tab already loaded.
export const useTermsBlockedReason = (contractId: string | undefined) => {
  const { t } = useTranslation();
  const { data: contract } = useContract(contractId);
  const { data: productsData, isLoading: productsLoading } = useProducts();
  const serviceItems = useMemo(
    () =>
      new Map(
        (productsData?.products ?? []).flatMap((product: ProductV2) =>
          (product.serviceItems ?? []).map(
            (item: ServiceItem) => [item.id, item] as const
          )
        )
      ),
    [productsData]
  );
  const blocker = contract ? termsBlocker(contract, serviceItems) : undefined;
  return {
    reason: blocker
      ? t(`contracts.proposals.termsBlocked.${blocker}`)
      : undefined,
    // A contract that failed to load stays held too: its terms are unknown.
    isLoading: !!contractId && (!contract || productsLoading)
  };
};

// Why Generate is unavailable on this deal, if it is. A closed deal, won or
// lost, can't generate one; otherwise it's the contract's Terms & Services.
export const useGenerateBlockedReason = (
  dealId: string | undefined,
  contractId: string | undefined
) => {
  const { t } = useTranslation();
  const { entities: deals, isLoading: dealLoading } = useDeals(
    dealId ? [dealId] : [],
    { enabled: !!dealId }
  );
  const deal = deals[0];
  const terms = useTermsBlockedReason(contractId);
  const closed =
    deal?.status === DealStatus.CLOSED_WON ||
    deal?.status === DealStatus.CLOSED_LOST;
  return {
    reason: closed
      ? t("contracts.proposals.generateRefused.dealClosed")
      : terms.reason,
    // Held while the deal loads, so a closed one never offers it. A deal that
    // fails to load doesn't hold it; contractsapi still refuses a closed one.
    isLoading: terms.isLoading || dealLoading
  };
};
